<script setup>
import { useI18n } from "vue-i18n";
import { FormErrors } from "@Components/Form";
import { computed, ref, onMounted } from "vue";
import Draggable from "vuedraggable";
import BlocksSidebar from "../BlocksSidebar.vue";
import { Block, BlockParametersModal } from "@Components/Blocks";
import { GripVerticalIcon } from "lucide-vue-next";
import { getBlock } from "@Utils/project";
import { PlusIcon } from "@heroicons/vue/24/outline";

const props = defineProps(["modelValue", "templates", "statuses", "categories", "postTypes", "users"]);
defineEmits(["update:modelValue"]);

const { t: __ } = useI18n();

const blockParametersModal = ref();
const currentBlockIndex = ref();

const currentBlock = computed(() => {
    return props.modelValue.modules.content.blocks[currentBlockIndex.value];
});

const drag = ref(false);

const addBlock = (block, index) => {
    let b = {
        block: block.key,
        settings: {},
    };

    if (block.parameters && block.parameters.length > 0) {
        block.parameters.forEach((parameter) => {
            b.settings[parameter.id] = parameter.default;
        });
    }

    if (index === undefined) {
        currentBlockIndex.value = props.modelValue.modules.content.blocks.length;
        props.modelValue.modules.content.blocks.push(b);
    }

    if (block.parameters && block.parameters.length > 0 && block.openModalAtStart) {
        if (typeof block.openModalAtStart === "function") {
            if (block.openModalAtStart(b)) {
                blockParametersModal.value.show();
            }
        } else if (block.openModalAtStart) {
            blockParametersModal.value.show();
        }
    }
};

const configureBlock = (index) => {
    currentBlockIndex.value = index;
    blockParametersModal.value.show();
};

const deleteBlock = (index) => {
    props.modelValue.modules.content.blocks.splice(index, 1);
};

const addHandler = ({ newIndex }) => {
    let block = getBlock(props.modelValue.modules.content.blocks[newIndex].block);

    if (block.parameters && block.parameters.length > 0 && block.openModalAtStart) {
        if (typeof block.openModalAtStart === "function") {
            if (block.openModalAtStart(props.modelValue.modules.content.blocks[newIndex])) {
                configureBlock(newIndex);
            }
        } else if (block.openModalAtStart) {
            configureBlock(newIndex);
        }
    }
};

const mounted = ref(false);
onMounted(() => {
    mounted.value = true;
});
</script>

<template>
    <div>
        <div class="grid grid-cols-2 gap-6">
            <div class="flex flex-col space-y-6">
                <div class="min-h-20 relative group">
                    <Draggable
                        :list="props.modelValue.modules.content.blocks"
                        tag="div"
                        :group="{ name: 'blocks' }"
                        :class="[drag && props.modelValue.modules.content.blocks.length === 0 ? 'h-20 justify-center' : '', 'min-h-20 flex flex-col space-y-3']"
                        handle=".oto-handle"
                        @add="addHandler">
                        <template #item="{ element: block, index }">
                            <div class="flex items-center space-x-1">
                                <div class="flex-none">
                                    <GripVerticalIcon
                                        class="oto-handle w-5 h-5 text-gray-400 cursor-move dark:text-gray-400 dark:hover:text-white hover:text-gray-600" />
                                </div>
                                <Block
                                    class="flex-1"
                                    v-model="props.modelValue.modules.content.blocks[index]"
                                    @configureBlock="configureBlock(index)"
                                    @deleteBlock="deleteBlock(index)" />
                            </div>
                        </template>
                    </Draggable>
                    <div
                        v-if="props.modelValue.modules.content.blocks.length === 0"
                        :class="[
                            drag ? 'dark:border-gray-600 border-gray-600' : 'border-gray-300 dark:border-gray-800',
                            'absolute inset-0 flex flex-col justify-center items-center rounded-lg border-2 border-dashed pointer-events-none',
                            'dark:group-hover:border-gray-600 group-hover:border-gray-600',
                        ]">
                        <template v-if="!drag">
                            <PlusIcon class="w-5 h-5" />
                            <span class="mt-1 text-sm font-medium leading-6 text-gray-900 dark:text-white">{{ __("Glissez un block ici") }}</span>
                        </template>
                    </div>
                </div>
                <FormErrors v-if="modelValue.getErrors('modules.blocks')" class="mt-2" :errors="modelValue.getErrors('modules.blocks')" />
            </div>
            <BlocksSidebar v-if="mounted" @addBlock="addBlock($event)" @start="drag = true" @end="drag = false" />
        </div>

        <BlockParametersModal ref="blockParametersModal" v-model="currentBlock" />
    </div>
</template>
